const msEvents = import('@shopify/events').catch(() => null);
if (!customElements.get('mio-art-selections')) {
  customElements.define('mio-art-selections', class extends HTMLElement {
    connectedCallback() {
      if (this.msConnected) return;
      this.msConnected = true;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.dialog = this.querySelector('.ms-dialog');
      this.content = this.querySelector('[data-ms-content]');
      this.rails = [...this.querySelectorAll('[data-ms-panel]')];
      this.raf = 0;
      this.resize = new ResizeObserver(() => this.queueUpdate());
      this.rails.forEach(panel => {
        const rail = panel.querySelector('[data-ms-rail]');
        if (!rail) return;
        rail.addEventListener('scroll', () => this.queueUpdate(), { passive: true, signal });
        rail.addEventListener('keydown', event => {
          if (event.target !== rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'Home' || event.key === 'End') rail.scrollTo({ left: event.key === 'Home' ? 0 : rail.scrollWidth, behavior: this.behavior() });
          else this.move(panel, event.key === 'ArrowLeft' ? -1 : 1);
        }, { signal });
        rail.addEventListener('pointerdown', e => { this.pointerX = e.clientX; this.pointerY = e.clientY; this.dragged = false; }, { passive: true, signal });
        rail.addEventListener('pointerup', e => { this.dragged = Math.hypot(e.clientX - this.pointerX, e.clientY - this.pointerY) > 12; }, { passive: true, signal });
        rail.addEventListener('pointercancel', () => { this.dragged = true; }, { signal });
        this.resize.observe(rail);
      });
      this.bindGalleries(signal);
      this.addEventListener('click', event => this.handleClick(event), { signal });
      this.addEventListener('change', event => this.variantChanged(event), { signal });
      this.addEventListener('submit', event => this.submit(event), { signal });
      this.dialog.addEventListener('close', () => this.afterClose(), { signal });
      this.queueUpdate();
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.afterClose();
      this.abort?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.raf);
      this.msConnected = false;
    }
    behavior() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
    queueUpdate() {
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => this.rails.forEach(panel => this.update(panel)));
    }
    update(panel) {
      const rail = panel.querySelector('[data-ms-rail]');
      if (!rail) return;
      const cards = [...rail.querySelectorAll('[data-ms-card]')];
      const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
      panel.querySelector('[data-ms-prev]').disabled = rail.scrollLeft < 3;
      panel.querySelector('[data-ms-next]').disabled = rail.scrollLeft >= max - 3;
      const edge = rail.getBoundingClientRect();
      const visible = cards.map((card, index) => {
        const box = card.getBoundingClientRect();
        const overlap = Math.min(box.right, edge.right) - Math.max(box.left, edge.left);
        return overlap >= box.width * .5 ? index + 1 : 0;
      }).filter(Boolean);
      if (visible.length) {
        panel.querySelector('[data-ms-counter]').textContent = (visible.length > 1 ? visible[0] + '–' + visible.at(-1) : visible[0]) + ' / ' + cards.length;
      }
      const controls = panel.querySelector('[data-ms-controls]');
      controls.hidden = max < 3;
      const hint = panel.querySelector('[data-ms-hint]');
      if (hint) hint.hidden = max < 3;
    }
    move(panel, direction) {
      const rail = panel.querySelector('[data-ms-rail]');
      const card = rail?.querySelector('[data-ms-card]');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(rail).gap) || 0;
      rail.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: this.behavior() });
    }
    bindGalleries(signal) {
      this.querySelectorAll('[data-ms-gallery]').forEach(media => {
        const count = Number(media.dataset.msImageCount) || 1;
        if (count < 2) return;
        const track = media.querySelector('[data-ms-gallery-track]');
        if (!track) return;
        let touch = null;
        const moved = event => {
          if (!touch) return;
          const point = [...event.changedTouches].find(item => item.identifier === touch.id);
          if (!point) return;
          if (Math.hypot(point.clientX - touch.x, point.clientY - touch.y) > 10) {
            this.dragged = true;
            media.msSwipeUntil = performance.now() + 600;
          }
        };
        media.addEventListener('touchstart', event => {
          const point = event.touches[0];
          touch = event.touches.length === 1 ? { x: point.clientX, y: point.clientY, id: point.identifier } : null;
          this.dragged = false;
          media.msSwipeUntil = 0;
        }, { passive: true, signal });
        media.addEventListener('touchmove', moved, { passive: true, signal });
        media.addEventListener('touchend', event => { moved(event); touch = null; }, { passive: true, signal });
        media.addEventListener('touchcancel', () => { touch = null; }, { passive: true, signal });
        track.addEventListener('scroll', () => {
          if (track.clientWidth) this.galleryState(media, Math.round(track.scrollLeft / track.clientWidth));
        }, { passive: true, signal });
        media.addEventListener('dragstart', event => event.preventDefault(), { signal });
        media.addEventListener('pointermove', event => {
          if (event.pointerType !== 'mouse' || event.buttons) return;
          const box = media.getBoundingClientRect();
          const fraction = Math.max(0, Math.min(.999, (event.clientX - box.left) / box.width));
          this.galleryImage(media, Math.floor(fraction * count));
        }, { signal });
        media.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          event.stopPropagation();
          this.galleryImage(media, Number(media.dataset.msImageIndex) + (event.key === 'ArrowRight' ? 1 : -1));
        }, { signal });
      });
    }
    galleryImage(media, index) {
      const track = media.querySelector('[data-ms-gallery-track]');
      const next = this.galleryState(media, index);
      if (track) track.scrollTo({ left: next * track.clientWidth, behavior: 'instant' });
    }
    galleryState(media, index) {
      const count = Number(media.dataset.msImageCount) || 1;
      const next = Math.max(0, Math.min(count - 1, index));
      media.dataset.msImageIndex = String(next);
      media.querySelectorAll('[data-ms-gallery-progress] > span').forEach((dot, i) => dot.classList.toggle('is-active', i === next));
      return next;
    }
    handleClick(event) {
      const prev = event.target.closest('[data-ms-prev]');
      const next = event.target.closest('[data-ms-next]');
      if (prev || next) { this.move((prev || next).closest('[data-ms-panel]'), prev ? -1 : 1); return; }
      const artworkLink = event.target.closest('.ms-media, .ms-card__title a');
      if (artworkLink) {
        if (event.button === 0 && event.detail !== 0 && (this.dragged || performance.now() < (artworkLink.msSwipeUntil || 0))) {
          event.preventDefault();
          this.dragged = false;
        }
        return;
      }
      const trigger = event.target.closest('[data-ms-open]');
      if (trigger && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        if (this.dragged || performance.now() < (trigger.msSwipeUntil || 0)) { event.preventDefault(); this.dragged = false; return; }
        const template = document.getElementById(trigger.dataset.msOpen);
        if (!template || typeof this.dialog.showModal !== 'function') return;
        event.preventDefault();
        this.returnFocus = trigger;
        this.content.replaceChildren(template.content.cloneNode(true));
        this.dialog.showModal();
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.locked = true;
        this.content.querySelector('.ms-preview__title')?.focus({ preventScroll: true });
        this.syncVariant();
        return;
      }
      if (event.target.closest('[data-ms-close]')) this.dialog.close();
      if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }
      const thumb = event.target.closest('[data-ms-image]');
      if (thumb) {
        this.setImage(thumb.dataset.msImage, thumb.dataset.msAlt);
        this.content.querySelectorAll('[data-ms-image]').forEach(b => b.setAttribute('aria-pressed', String(b === thumb)));
      }
    }
    afterClose() {
      if (this.locked) document.documentElement.style.overflow = this.previousOverflow;
      this.locked = false;
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }
    setImage(src, alt) {
      const image = this.content.querySelector('.ms-large-image');
      if (!image || !src) return;
      image.removeAttribute('srcset');
      image.src = src;
      if (alt) image.alt = alt;
    }
    variantChanged(event) { if (event.target.matches('[data-ms-variant]')) this.syncVariant(); }
    syncVariant() {
      const select = this.content.querySelector('[data-ms-variant]');
      if (!select) return;
      const option = select.selectedOptions[0];
      if (!option) return;
      this.content.querySelector('[data-ms-price]').textContent = option.dataset.price;
      const quantity = this.content.querySelector('[name="quantity"]');
      if (quantity) quantity.value = option.dataset.min || '1';
      if (option.dataset.image) this.setImage(option.dataset.image);
      const add = this.content.querySelector('.ms-add');
      if (add) add.disabled = option.disabled;
    }
    async submit(event) {
      if (!event.target.matches('.ms-form')) return;
      event.preventDefault();
      const form = event.target;
      if (form.dataset.busy === 'true') return;
      const select = form.querySelector('[name="id"]');
      if (!select?.value || select.selectedOptions[0]?.disabled) return;
      const button = form.querySelector('.ms-add');
      const feedback = form.parentElement.querySelector('[data-ms-feedback]');
      const variant = select.value;
      const quantity = Number(form.querySelector('[name="quantity"]').value) || 1;
      form.dataset.busy = 'true';
      button.disabled = true;
      select.disabled = true;
      button.textContent = this.dataset.adding;
      feedback.textContent = '';
      feedback.removeAttribute('data-error');
      const root = window.Shopify?.routes?.root || '/';
      const ids = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
      const events = await msEvents;
      const deferred = events?.CartLinesUpdateEvent?.createPromise();
      deferred?.promise.catch(() => {});
      if (deferred) {
        form.dispatchEvent(new events.CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: variant, quantity }], promise: deferred.promise }));
      }
      let added = false;
      try {
        const response = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variant, quantity }], sections: ids, sections_url: location.pathname }), credentials: 'same-origin' });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.error);
        added = true;
        feedback.textContent = this.dataset.added;
        this.querySelector('[data-ms-announcement]').textContent = this.dataset.added;
        const cartResponse = await fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error(this.dataset.error);
        const cart = await cartResponse.json();
        deferred?.resolve({ cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'mio-art-selections', sections: result.sections, didError: false, itemCount: quantity, productId: this.content.querySelector('[data-product-id]')?.dataset.productId } });
        document.dispatchEvent(new CustomEvent('mio:cart-added', { detail: { cart } }));
        if (document.querySelector('cart-drawer-component[auto-open]') && this.dialog.open) this.dialog.close();
      } catch (error) {
        deferred?.reject(error);
        feedback.textContent = added ? this.dataset.added : (error.message || this.dataset.error);
        if (!added) feedback.setAttribute('data-error', '');
      } finally {
        form.dataset.busy = 'false';
        select.disabled = false;
        button.disabled = false;
        button.textContent = this.dataset.add;
      }
    }
  });
}
